import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "d-flex align-center justify-space-between mb-1" }
const _hoisted_2 = { class: "d-flex align-center justify-space-between mb-1" }
const _hoisted_3 = { class: "pb-map-wrapper flex-grow-1 position-relative" }
const _hoisted_4 = {
  key: 0,
  class: "pb-empty-overlay pa-4 text-center"
}
const _hoisted_5 = {
  key: 1,
  class: "pb-floating-clock pa-2 px-3"
}
const _hoisted_6 = { class: "d-flex align-center ga-2" }
const _hoisted_7 = { class: "pb-clock-time" }
const _hoisted_8 = { class: "pb-clock-info text-truncate" }
const _hoisted_9 = { class: "d-flex flex-column ga-2" }
const _hoisted_10 = { class: "d-flex align-center ga-3" }
const _hoisted_11 = { class: "text-caption text-grey font-weight-bold pb-font-mono" }
const _hoisted_12 = { class: "text-caption text-grey font-weight-bold pb-font-mono" }
const _hoisted_13 = { class: "d-flex align-center justify-space-between" }
const _hoisted_14 = { class: "d-flex align-center ga-2" }
const _hoisted_15 = { class: "d-flex align-center ga-1" }

import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import camionIcon from '@/assets/Icons/camion.jpg';
import solartrailerIcon from '@/assets/Icons/solartrailer.jpg';
import CONSTMAPS from '@/constants/maps';
import { getTelemetryHistory, downloadTelemetryCSV, getInventoryDevices, getAccesoDevices, TelemetryPoint } from '@/services/Cementera/Telemetry';

const MAX_EQUIPMENT_LIMIT = 10;

// REFERENCIAS Y MAPAS DE DATOS

export default /*@__PURE__*/_defineComponent({
  __name: 'PlaybackView',
  setup(__props) {

const mapContainerRef = ref<HTMLElement | null>(null);
let playbackMapInstance: maplibregl.Map | null = null;
let pbActiveMarkers: Map<string, maplibregl.Marker> = new Map();

// DICCIONARIO DE COLORES ASIGNADOS A CADA IP DE CAMIÓN
const ipColorMap = new Map<string, string>();

// ESTADOS DE FILTRO
const selectedDate = ref(new Date().toISOString().slice(0, 10));
const selectedTurn = ref('ALL');
const selectedIps = ref<string[]>([]);
const selectedAccesoIps = ref<string[]>([]);

// INVENTARIO COMPLETO
const allInventoryDevices = ref<{ ip: string; name: string }[]>([]);
const allAccesoDevices = ref<{ ip: string; name: string }[]>([]);

const loadingData = ref(false);
const isPlaying = ref(false);
const playbackSpeed = ref(5);
const sliderIndex = ref(0);
let animationTimer: ReturnType<typeof setInterval> | null = null;

const telemetryPoints = ref<TelemetryPoint[]>([]);

// OPCIONES DE TURNO
const turnOptions = [
    { title: 'Día Completo (24 hrs)', value: 'ALL' },
    { title: 'Turno Día (DDd 07:00-15:00)', value: 'DDd' },
    { title: 'Turno Tarde (DDa 15:00-23:00)', value: 'DDa' },
    { title: 'Turno Noche (DDn 23:00-07:00)', value: 'DDn' }
];

// LISTADO DE EQUIPOS COMPLETO PARA BÚSQUEDA
const deviceOptions = computed(() => {
    return allInventoryDevices.value.map(d => ({
        title: `${d.name || 'Equipo'} (${d.ip})`,
        value: d.ip
    }));
});

const accesoOptions = computed(() => {
    return allAccesoDevices.value.map(d => ({
        title: `${d.name || 'Equipo'} (${d.ip})`,
        value: d.ip
    }));
});

// LIMITAR LA SELECCIÓN MÁXIMA A 10 EQUIPOS
function onSelectedIpsChange(val: string[]) {
    if (val && val.length > MAX_EQUIPMENT_LIMIT) {
        selectedIps.value = val.slice(0, MAX_EQUIPMENT_LIMIT);
    }
}

function onSelectedAccesoIpsChange(val: string[]) {
    if (val && val.length > MAX_EQUIPMENT_LIMIT) {
        selectedAccesoIps.value = val.slice(0, MAX_EQUIPMENT_LIMIT);
    }
}

// COMPUTADOS DE TIEMPO
const uniqueTimestamps = computed(() => {
    const set = new Set<string>();
    telemetryPoints.value.forEach(p => set.add(p.fecha));
    return Array.from(set).sort();
});

const maxSliderIndex = computed(() => Math.max(0, uniqueTimestamps.value.length - 1));

const currentFormattedTime = computed(() => {
    if (uniqueTimestamps.value.length === 0) return '00:00:00';
    return uniqueTimestamps.value[sliderIndex.value] || '00:00:00';
});

const totalEquipsCount = computed(() => {
    const ips = new Set(telemetryPoints.value.map(p => p.ip));
    return ips.size;
});

const activeEquipsCount = computed(() => {
    const currentTs = uniqueTimestamps.value[sliderIndex.value];
    if (!currentTs) return 0;
    const active = telemetryPoints.value.filter(p => p.fecha === currentTs);
    return new Set(active.map(p => p.ip)).size;
});

function formatTimeOnly(dateStr?: string) {
    if (!dateStr) return '00:00';
    const parts = dateStr.split(' ');
    return parts.length >= 2 ? parts[1].substring(0, 5) : dateStr;
}

// CARGAR EL CATÁLOGO COMPLETO DE CAMIONES Y EQUIPOS DE ACCESO
async function loadDevicesList() {
    const [devices, accesoDevices] = await Promise.all([
        getInventoryDevices(),
        getAccesoDevices()
    ]);
    allInventoryDevices.value = devices;
    allAccesoDevices.value = accesoDevices;
}

// INICIALIZACIÓN DEL MAPA
function initPlaybackMap() {
    const containerEl = mapContainerRef.value || document.getElementById('mapPlaybackContainer');

    if (!containerEl || playbackMapInstance) return;

    try {
        playbackMapInstance = new maplibregl.Map({
            container: containerEl,
            center: [CONSTMAPS.longitud.value, CONSTMAPS.latitud.value],
            zoom: 14,
            pitch: 30,
            style: 'https://api.maptiler.com/maps/satellite/style.json?key=6JWVt6LFaY22nquimZpF'
        });

        playbackMapInstance.addControl(new maplibregl.NavigationControl(), 'top-right');

        playbackMapInstance.on('load', () => {
            setTimeout(() => {
                if (playbackMapInstance) {
                    playbackMapInstance.resize();
                }
            }, 150);
        });
    } catch (err) {
        console.error("Error al inicializar el mapa de Playback:", err);
    }
}

// CARGA MANUAL DE DATOS DE HISTORIAL
async function loadHistory() {
    pausePlayback();
    loadingData.value = true;

    // Construir lista de IPs a consultar
    const ipsToQuery: string[] = [];

    // Equipos cliente: si no hay selección, usar primeras 10 del inventario
    if (selectedIps.value.length > 0) {
        ipsToQuery.push(...selectedIps.value);
    } else if (allInventoryDevices.value.length > 0) {
        ipsToQuery.push(...allInventoryDevices.value.map(d => d.ip).slice(0, MAX_EQUIPMENT_LIMIT));
    }

    // Equipos acceso: solo si hay selección (es opcional)
    if (selectedAccesoIps.value.length > 0) {
        ipsToQuery.push(...selectedAccesoIps.value);
    }

    let targetIps: string | string[] = 'ALL';
    if (ipsToQuery.length > 0) {
        targetIps = ipsToQuery;
    }

    const res = await getTelemetryHistory(selectedDate.value, selectedTurn.value, targetIps);
    loadingData.value = false;

    if (res && res.data && res.data.length > 0) {
        let rawPoints = res.data;

        // Filtrar solo equipos válidos (cliente o acceso seleccionados)
        const validClientIps = new Set(allInventoryDevices.value.map(d => d.ip));
        const validAccesoIps = new Set(selectedAccesoIps.value);
        const validIps = new Set([...validClientIps, ...validAccesoIps]);
        rawPoints = rawPoints.filter(p => validIps.has(p.ip));

        telemetryPoints.value = rawPoints;
        sliderIndex.value = 0;
        await nextTick();
        drawRoutesOnMap();
        updateMarkersPosition();
    } else {
        telemetryPoints.value = [];
        clearMapLayers();
    }
}

function handleExportCSV() {
    downloadTelemetryCSV(selectedDate.value, selectedTurn.value);
}

// CAPAS Y DIBUJO DE RUTAS
function clearMapLayers() {
    pbActiveMarkers.forEach(m => m.remove());
    pbActiveMarkers.clear();
    ipColorMap.clear();

    if (!playbackMapInstance) return;

    try {
        const style = playbackMapInstance.getStyle();
        if (style && style.layers) {
            style.layers.forEach(layer => {
                if (layer.id.startsWith('pb-route-line-')) {
                    playbackMapInstance?.removeLayer(layer.id);
                }
            });
        }

        if (style && style.sources) {
            Object.keys(style.sources).forEach(sourceId => {
                if (sourceId.startsWith('pb-route-source-')) {
                    playbackMapInstance?.removeSource(sourceId);
                }
            });
        }
    } catch (e) {
        console.error("Error limpiando capas:", e);
    }
}

function drawRoutesOnMap() {
    if (!playbackMapInstance) return;
    clearMapLayers();

    const groupedByIp = new Map<string, [number, number][]>();

    telemetryPoints.value.forEach(p => {
        if (!groupedByIp.has(p.ip)) groupedByIp.set(p.ip, []);
        groupedByIp.get(p.ip)!.push([Number(p.longitud), Number(p.latitud)]);
    });

    const colors = [
        '#00E676', // Verde Neón
        '#00E5FF', // Cían Neón
        '#FFEA00', // Amarillo
        '#FF3D00', // Rojo Ciber
        '#AB47BC', // Púrpura
        '#29B6F6', // Azul Celeste
        '#EC407A', // Rosado
        '#26A69A', // Menta
        '#FFA726', // Naranja
        '#8D6E63'  // Marrón
    ];
    let colorIdx = 0;

    groupedByIp.forEach((coords, ip) => {
        if (coords.length < 2) return;

        const sourceId = `pb-route-source-${ip}`;
        const layerId = `pb-route-line-${ip}`;
        const color = colors[colorIdx % colors.length];

        // GUARDAR COLOR ASOCIADO A ESTA IP
        ipColorMap.set(ip, color);
        colorIdx++;

        playbackMapInstance?.addSource(sourceId, {
            type: 'geojson',
            data: {
                type: 'Feature',
                properties: { ip },
                geometry: { type: 'LineString', coordinates: coords }
            }
        });

        playbackMapInstance?.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.85 }
        });
    });

    if (telemetryPoints.value.length > 0) {
        const first = telemetryPoints.value[0];
        playbackMapInstance.flyTo({
            center: [Number(first.longitud), Number(first.latitud)],
            zoom: 14
        });
    }
}

function updateMarkersPosition() {
    if (!playbackMapInstance || uniqueTimestamps.value.length === 0) return;

    const currentTs = uniqueTimestamps.value[sliderIndex.value];
    if (!currentTs) return;

    const currentPoints = telemetryPoints.value.filter(p => p.fecha === currentTs);

    currentPoints.forEach(p => {
        const lng = Number(p.longitud);
        const lat = Number(p.latitud);
        
        // RECUPERAR EL COLOR ASIGNADO A ESTA IP (O CÍAN POR DEFECTO)
        const truckColor = ipColorMap.get(p.ip) || '#00E5FF';

        if (pbActiveMarkers.has(p.ip)) {
            pbActiveMarkers.get(p.ip)!.setLngLat([lng, lat]);
        } else {
            const isAcceso = selectedAccesoIps.value.includes(p.ip);
            const iconSrc = isAcceso ? solartrailerIcon : camionIcon;
            const iconAlt = isAcceso ? 'Equipo Acceso' : 'Camión';

            const el = document.createElement('div');
            el.className = 'pb-truck-marker';
            el.innerHTML = `
                <img src="${iconSrc}" class="pb-truck-img" alt="${iconAlt}" />
                <div class="pb-marker-name" style="color: ${truckColor}; border-color: ${truckColor}; text-shadow: 0 0 6px ${truckColor}aa;">
                    ${p.name || p.ip}
                </div>
            `;

            const marker = new maplibregl.Marker({ element: el })
                .setLngLat([lng, lat])
                .addTo(playbackMapInstance!);

            pbActiveMarkers.set(p.ip, marker);
        }
    });
}

// CONTROLES MULTIMEDIA
function togglePlay() {
    if (isPlaying.value) {
        pausePlayback();
    } else {
        startPlayback();
    }
}

function startPlayback() {
    if (uniqueTimestamps.value.length === 0) return;
    isPlaying.value = true;

    if (sliderIndex.value >= maxSliderIndex.value) {
        sliderIndex.value = 0;
    }

    const intervalMs = Math.max(50, 1000 / playbackSpeed.value);

    animationTimer = setInterval(() => {
        if (sliderIndex.value < maxSliderIndex.value) {
            sliderIndex.value++;
            updateMarkersPosition();
        } else {
            pausePlayback();
        }
    }, intervalMs);
}

function pausePlayback() {
    isPlaying.value = false;
    if (animationTimer) {
        clearInterval(animationTimer);
        animationTimer = null;
    }
}

function restartPlayback() {
    pausePlayback();
    sliderIndex.value = 0;
    updateMarkersPosition();
}

function setSpeed(speed: number) {
    playbackSpeed.value = speed;
    if (isPlaying.value) {
        pausePlayback();
        startPlayback();
    }
}

function onSliderChange() {
    updateMarkersPosition();
}

// CICLO DE VIDA DE VUE
onMounted(() => {
    loadDevicesList();
    nextTick(() => {
        setTimeout(() => {
            initPlaybackMap();
        }, 200);
    });
});

onUnmounted(() => {
    pausePlayback();
    clearMapLayers();
    if (playbackMapInstance) {
        playbackMapInstance.remove();
        playbackMapInstance = null;
    }
});

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_slider = _resolveComponent("v-slider")!

  return (_openBlock(), _createBlock(_component_v_card, {
    class: "defaultCard pa-3 h-100 d-flex flex-column",
    color: "bgtopology",
    flat: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, {
        color: "#0d131f",
        class: "pa-3 mb-3 pb-border-subtle",
        flat: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_row, { class: "ma-0 align-center" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                sm: "6",
                md: "3",
                class: "pa-2"
              }, {
                default: _withCtx(() => [
                  _cache[5] || (_cache[5] = _createElementVNode("span", { class: "pb-filter-label" }, "Fecha", -1)),
                  _createVNode(_component_v_text_field, {
                    modelValue: selectedDate.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedDate).value = $event)),
                    type: "date",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    class: "pb-neon-input"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                sm: "6",
                md: "3",
                class: "pa-2"
              }, {
                default: _withCtx(() => [
                  _cache[6] || (_cache[6] = _createElementVNode("span", { class: "pb-filter-label" }, "Turno", -1)),
                  _createVNode(_component_v_select, {
                    modelValue: selectedTurn.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedTurn).value = $event)),
                    items: turnOptions,
                    "item-title": "title",
                    "item-value": "value",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    class: "pb-neon-input"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                sm: "6",
                md: "3",
                class: "pa-2"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_1, [
                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "pb-filter-label" }, "Equipos Cliente", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["pb-count-badge", { 'pb-count-limit': selectedIps.value.length >= 10 }])
                    }, _toDisplayString(selectedIps.value.length) + " / 10 Máx. ", 3)
                  ]),
                  _createVNode(_component_v_autocomplete, {
                    modelValue: selectedIps.value,
                    "onUpdate:modelValue": [
                      _cache[2] || (_cache[2] = ($event: any) => ((selectedIps).value = $event)),
                      onSelectedIpsChange
                    ],
                    items: deviceOptions.value,
                    "item-title": "title",
                    "item-value": "value",
                    multiple: "",
                    chips: "",
                    "closable-chips": "",
                    clearable: "",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    placeholder: "Buscar camiones...",
                    "no-data-text": "No se encontraron camiones",
                    class: "pb-neon-input"
                  }, null, 8, ["modelValue", "items"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                sm: "6",
                md: "3",
                class: "pa-2"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_2, [
                    _cache[8] || (_cache[8] = _createElementVNode("span", { class: "pb-filter-label" }, "Equipos Acceso", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["pb-count-badge", { 'pb-count-limit': selectedAccesoIps.value.length >= 10 }])
                    }, _toDisplayString(selectedAccesoIps.value.length) + " / 10 Máx. ", 3)
                  ]),
                  _createVNode(_component_v_autocomplete, {
                    modelValue: selectedAccesoIps.value,
                    "onUpdate:modelValue": [
                      _cache[3] || (_cache[3] = ($event: any) => ((selectedAccesoIps).value = $event)),
                      onSelectedAccesoIpsChange
                    ],
                    items: accesoOptions.value,
                    "item-title": "title",
                    "item-value": "value",
                    multiple: "",
                    chips: "",
                    "closable-chips": "",
                    clearable: "",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    placeholder: "Buscar equipos acceso...",
                    "no-data-text": "No se encontraron equipos de acceso",
                    class: "pb-neon-input"
                  }, null, 8, ["modelValue", "items"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, { class: "ma-0 ga-3 mt-1" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                class: "pa-0 d-flex justify-end ga-2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    color: "cyan-accent-3",
                    variant: "flat",
                    size: "small",
                    class: "font-weight-black",
                    loading: loadingData.value,
                    onClick: loadHistory
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        start: "",
                        size: "small"
                      }, {
                        default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                          _createTextVNode("mdi-database-search", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[10] || (_cache[10] = _createTextVNode(" Cargar Datos ", -1))
                    ]),
                    _: 1
                  }, 8, ["loading"]),
                  _createVNode(_component_v_btn, {
                    color: "success",
                    variant: "outlined",
                    size: "small",
                    class: "font-weight-bold",
                    disabled: telemetryPoints.value.length === 0,
                    onClick: handleExportCSV
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        start: "",
                        size: "small"
                      }, {
                        default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                          _createTextVNode("mdi-file-delimited", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[12] || (_cache[12] = _createTextVNode(" Exportar CSV ", -1))
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          ref_key: "mapContainerRef",
          ref: mapContainerRef,
          id: "mapPlaybackContainer",
          class: "pb-map-element"
        }, null, 512),
        (telemetryPoints.value.length === 0 && !loadingData.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_component_v_icon, {
                color: "cyan-accent-3",
                size: "large",
                class: "mb-2"
              }, {
                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                  _createTextVNode("mdi-radar", -1)
                ]))]),
                _: 1
              }),
              _cache[14] || (_cache[14] = _createElementVNode("div", { class: "text-subtitle-2 font-weight-bold text-white" }, "Consola de Replicación Histórica", -1)),
              _cache[15] || (_cache[15] = _createElementVNode("div", { class: "text-caption text-grey-lighten-1 mt-1" }, [
                _createTextVNode(" Selecciona hasta "),
                _createElementVNode("b", null, "10 equipos cliente"),
                _createTextVNode(" y/o "),
                _createElementVNode("b", null, "10 equipos acceso"),
                _createTextVNode(" por Nombre o IP, luego haz clic en "),
                _createElementVNode("b", null, "\"Cargar Datos\""),
                _createTextVNode(". ")
              ], -1))
            ]))
          : _createCommentVNode("", true),
        (uniqueTimestamps.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _cache[16] || (_cache[16] = _createElementVNode("span", { class: "pb-clock-live-dot" }, null, -1)),
                _createElementVNode("span", _hoisted_7, _toDisplayString(currentFormattedTime.value), 1)
              ]),
              _createElementVNode("div", _hoisted_8, [
                _cache[17] || (_cache[17] = _createTextVNode(" Activos: ", -1)),
                _createElementVNode("b", null, _toDisplayString(activeEquipsCount.value), 1),
                _createTextVNode(" / " + _toDisplayString(totalEquipsCount.value), 1)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(_component_v_card, {
        color: "#0d131f",
        class: "pa-3 mt-3 pb-border-subtle",
        flat: ""
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("span", _hoisted_11, _toDisplayString(formatTimeOnly(uniqueTimestamps.value[0])), 1),
              _createVNode(_component_v_slider, {
                modelValue: sliderIndex.value,
                "onUpdate:modelValue": [
                  _cache[4] || (_cache[4] = ($event: any) => ((sliderIndex).value = $event)),
                  onSliderChange
                ],
                min: 0,
                max: maxSliderIndex.value,
                step: 1,
                "hide-details": "",
                color: "cyan-accent-3",
                "track-color": "rgba(255,255,255,0.1)",
                class: "flex-grow-1",
                disabled: uniqueTimestamps.value.length === 0
              }, null, 8, ["modelValue", "max", "disabled"]),
              _createElementVNode("span", _hoisted_12, _toDisplayString(formatTimeOnly(uniqueTimestamps.value[maxSliderIndex.value])), 1)
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _createVNode(_component_v_btn, {
                  icon: "mdi-rewind",
                  size: "x-small",
                  variant: "tonal",
                  color: "grey",
                  disabled: uniqueTimestamps.value.length === 0,
                  onClick: restartPlayback
                }, null, 8, ["disabled"]),
                _createVNode(_component_v_btn, {
                  icon: isPlaying.value ? 'mdi-pause' : 'mdi-play',
                  color: "cyan-accent-3",
                  size: "small",
                  class: "pb-glow-btn",
                  disabled: uniqueTimestamps.value.length === 0,
                  onClick: togglePlay
                }, null, 8, ["icon", "disabled"])
              ]),
              _createElementVNode("div", _hoisted_15, [
                _cache[18] || (_cache[18] = _createElementVNode("span", { class: "text-caption text-grey-lighten-1 mr-2 font-weight-bold" }, "Velocidad:", -1)),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList([1, 2, 5, 10, 20], (speed) => {
                  return _createVNode(_component_v_btn, {
                    key: speed,
                    size: "x-small",
                    color: playbackSpeed.value === speed ? 'cyan-accent-3' : 'grey-darken-3',
                    variant: playbackSpeed.value === speed ? 'flat' : 'tonal',
                    class: "font-weight-black",
                    onClick: ($event: any) => (setSpeed(speed))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(speed) + "x ", 1)
                    ]),
                    _: 2
                  }, 1032, ["color", "variant", "onClick"])
                }), 64))
              ])
            ])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})